import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const EMPTY_FORM = {
  ID: null,
  SORU: "",
  CEVAP: "",
  SIRA: 0,
  KATEGORI: "",
  AKTIF: true,
};

const SSSYonetimi = () => {
  document.title = "SSS Yönetimi | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [liste, setListe] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchListe = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/SSS?$orderby=SIRA asc,ID desc`, { headers });
      const result = await res.json();
      setListe(result.value || []);
    } catch {
      setError("SSS listesi yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchListe(); }, []);

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setModal(true);
  };

  const handleEdit = (row) => {
    setFormData({
      ID: row.ID,
      SORU: row.SORU || "",
      CEVAP: row.CEVAP || "",
      SIRA: row.SIRA ?? 0,
      KATEGORI: row.KATEGORI || "",
      AKTIF: row.AKTIF !== false,
    });
    setIsEditing(true);
    setModal(true);
  };

  const handleSave = async () => {
    if (!formData.SORU.trim() || !formData.CEVAP.trim()) {
      setError("Soru ve cevap zorunludur.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        SORU: formData.SORU,
        CEVAP: formData.CEVAP,
        SIRA: Number(formData.SIRA) || 0,
        KATEGORI: formData.KATEGORI || null,
        AKTIF: formData.AKTIF,
        GUNCELLEME_TARIHI: new Date().toISOString(),
      };

      if (isEditing) {
        await fetch(`${BASE_URL}/SSS(${formData.ID})`, {
          method: "PATCH", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("SSS güncellendi.");
      } else {
        await fetch(`${BASE_URL}/SSS`, {
          method: "POST", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("SSS eklendi.");
      }

      setModal(false);
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Kayıt sırasında hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu SSS kaydını silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/SSS(${id})`, { method: "DELETE", headers });
      setSuccessMsg("SSS silindi.");
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "#",
      accessorKey: "ID",
      enableSorting: true,
      cell: (c) => <strong>#{c.getValue()}</strong>,
    },
    {
      header: "Sıra",
      accessorKey: "SIRA",
      enableSorting: true,
      cell: (c) => <Badge color="light" className="text-dark">{c.getValue() ?? 0}</Badge>,
    },
    {
      header: "Soru",
      accessorKey: "SORU",
      enableSorting: true,
      cell: (c) => <span className="fw-medium">{c.getValue() || "---"}</span>,
    },
    {
      header: "Kategori",
      accessorKey: "KATEGORI",
      enableSorting: true,
      cell: (c) => c.getValue()
        ? <Badge color="info" pill>{c.getValue()}</Badge>
        : <span className="text-muted">Genel</span>,
    },
    {
      header: "Durum",
      accessorKey: "AKTIF",
      enableSorting: false,
      cell: (c) => (
        <Badge color={c.getValue() ? "success" : "danger"}>
          {c.getValue() ? "Aktif" : "Pasif"}
        </Badge>
      ),
    },
    {
      header: "İşlemler",
      enableSorting: false,
      cell: (cellProps) => {
        const row = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => handleEdit(row)}>
              <i className="bx bx-edit" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(row.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  const f = (field, val) => setFormData(p => ({ ...p, [field]: val }));

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Yönetim" breadcrumbItem="SSS Yönetimi" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h4 className="card-title mb-1">Sıkça Sorulan Sorular</h4>
                      <p className="text-muted small mb-0">
                        Mobil uygulamada SSS ekranında listelenecek soruları buradan yönetin.
                      </p>
                    </div>
                    <Button color="primary" size="sm" onClick={openAdd}>
                      <i className="bx bx-plus me-1" />Yeni Soru
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={liste}
                      isGlobalFilter={true}
                      isPagination={true}
                      customPageSize={15}
                      SearchPlaceholder="Soru ara..."
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination pagination-rounded justify-content-end mb-2"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="lg">
        <ModalHeader toggle={() => setModal(false)}>
          <i className={`mdi ${isEditing ? "mdi-comment-question-outline" : "mdi-comment-plus-outline"} me-2 text-primary`}></i>
          {isEditing ? `SSS Düzenle (#${formData.ID})` : "Yeni SSS Ekle"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Soru <span className="text-danger">*</span></Label>
                <Input
                  value={formData.SORU}
                  onChange={(e) => f("SORU", e.target.value)}
                  placeholder="Örn: Hak satın aldığımda nasıl kullanırım?"
                  maxLength={500}
                />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Cevap <span className="text-danger">*</span></Label>
                <Input
                  type="textarea"
                  rows={6}
                  value={formData.CEVAP}
                  onChange={(e) => f("CEVAP", e.target.value)}
                  placeholder="Cevabı detaylı yazın..."
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Kategori (isteğe bağlı)</Label>
                <Input
                  value={formData.KATEGORI}
                  onChange={(e) => f("KATEGORI", e.target.value)}
                  placeholder="Genel / Ödeme / Mezat / İlan ..."
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Sıra No</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.SIRA}
                  onChange={(e) => f("SIRA", e.target.value)}
                  placeholder="0"
                />
                <small className="text-muted">Küçük olan üstte görünür.</small>
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup check className="mt-1">
                <Input
                  type="checkbox"
                  id="sssAktif"
                  checked={formData.AKTIF}
                  onChange={(e) => f("AKTIF", e.target.checked)}
                />
                <Label check for="sssAktif" className="fw-bold ms-1">
                  Aktif — mobilde gösterilsin
                </Label>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)} disabled={isSaving}>İptal</Button>
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-content-save me-1"></i>}
            {isEditing ? "Güncelle" : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SSSYonetimi;
